/* eslint-disable lingui/no-unlocalized-strings */
import { CircleNotch, FileJs, FilePdf } from "@phosphor-icons/react";
import {
  buttonVariants,
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { usePrintResume } from "@/client/services/resume/print";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

export const ExportDialog = () => {
  const { isOpen, close } = useDialog("export");
  const { toast } = useToast();
  const { printResume, loading: isPrinting } = usePrintResume();
  const [loading, setLoading] = useState(false);

  const id = useResumeStore((state) => state.resume.id);
  const resume = useResumeStore((state) => state.resume);

  const onJsonExport = () => {
    const dataStr = JSON.stringify(resume, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;

    const exportFileDefaultName = `resume-${resume.id}.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();

    close();
  };

  const onPdfExport = async () => {
    try {
      setLoading(true);

      // 检查当前模板是否为付费模板
      const response = await fetch(
        `/api/subscription/check-template/${resume.data.metadata.template}`,
      );
      const { isPremiumTemplate, isAllowed } = await response.json();

      if (isPremiumTemplate && !isAllowed) {
        toast({
          variant: "error",
          title: "会员专属模板",
          description: "当前使用的是会员专属模板，请升级会员后下载",
        });

        close();
        return;
      }

      await printResume({ id });
      close();
    } catch (error) {
      console.error("Error during PDF export:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>导出简历</DialogTitle>
        </DialogHeader>

        <div className="mt-2 grid gap-4">
          <Card
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
            )}
            onClick={onJsonExport}
          >
            <FileJs size={22} />
            <CardContent className="flex-1">
              <CardTitle className="text-sm">{`JSON`}</CardTitle>
              <CardDescription className="font-normal">
                {`下载 JSON 格式的简历快照。此文件可用于将来导入简历。`}
              </CardDescription>
            </CardContent>
          </Card>

          <Card
            className={cn(
              buttonVariants({ variant: "ghost" }),
              "h-auto cursor-pointer flex-row items-center gap-x-5 px-4 pb-3 pt-1",
              (loading || isPrinting) && "pointer-events-none cursor-progress opacity-75",
            )}
            onClick={onPdfExport}
          >
            {loading || isPrinting ? (
              <CircleNotch size={22} className="animate-spin" />
            ) : (
              <FilePdf size={22} />
            )}

            <CardContent className="flex-1">
              <CardTitle className="text-sm">{`PDF`}</CardTitle>
              <CardDescription className="font-normal">
                {`下载 PDF 格式的简历。此文件可用于打印简历、发送给招聘人员或上传至招聘网站。部分模板仅限会员使用。`}
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
